/**
 * TravelHome — /travel  (stub / preview page)
 * Shows the vision so users know what's coming. Individual sections
 * link to placeholder listings using the existing business directory
 * (hotels, tour operators, car rentals) filtered by industry.
 */
import { Link } from "react-router-dom";
import {
  Plane, Hotel, Train, Bus, Ship, Car, MapPin, Building2, Sparkles, ArrowRight, Compass,
} from "lucide-react";
import TravelChipBar from "./travel/TravelChipBar";
import TravelCategoryNav from "@/components/TravelCategoryNav";
import TopicNewsStrip from "@/components/TopicNewsStrip";

const CATEGORIES = [
  { icon: Hotel, label: "Hotels & Accommodations", to: "/businesses?industry=hotel", body: "Hotels, resorts, homestays, vacation rentals, houseboats, and more." },
  { icon: Plane, label: "Flights & Airlines",     to: "/travel/airlines", body: "Airline pages, airport guides, baggage info, loyalty programs." },
  { icon: Building2, label: "Airports",            to: "/travel/airports", body: "Every airport gets facilities, lounges, taxi info, nearby hotels." },
  { icon: Train, label: "Rail Travel",             to: "/travel/rail",     body: "Rail operators, tourist trains, metros, heritage & luxury trains." },
  { icon: Train, label: "Rail Stations",           to: "/travel/rail-stations", body: "Central stations, heritage terminals, connecting hubs." },
  { icon: Bus,   label: "Bus Terminals",           to: "/travel/bus",      body: "Terminals, intercity buses, luxury coaches, shuttle services." },
  { icon: Ship,  label: "Cruises & Ferries",       to: "/travel/cruises",  body: "River, ocean, houseboats, ferry services, island transfers." },
  { icon: Ship,  label: "Seaports & Cargo Ports",  to: "/travel/seaports", body: "Major container, cruise, and cargo ports around the world." },
  { icon: Car,   label: "Taxi & Rideshare",        to: "/travel/taxi",     body: "Uber, Lyft, Ola, Grab, Bolt, Rapido, DiDi and local taxi operators." },
  { icon: Car,   label: "Car Rentals",             to: "/travel/rentals",  body: "Car rentals, chauffeur services, self-drive, motorcycles, camper vans.", stub: true },
  { icon: Compass, label: "Tours & Local Experiences", to: "/travel/tours", body: "Cooking classes, village tours, heritage walks, wildlife safaris.", stub: true },
];

export default function TravelHome() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="travel-home">
      <TravelCategoryNav />

      {/* Latest Travel news */}
      <TopicNewsStrip topic="Travel" q="travel" testId="travel-news-strip" />

      <section className="rounded-xl p-6 md:p-10 mb-8 mt-4" style={{ background: "var(--wofy-surface)" }}>
        <div className="overline mb-2">SOCIAL PULSE TRAVEL</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">Explore. Plan. Book. Experience.</h1>
        <p className="max-w-2xl opacity-80">
          Whether you're planning a family vacation, a business trip, a pilgrimage, or a weekend
          getaway — Social Pulse Travel connects you with destinations, trusted travel providers,
          and local communities.
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}>
          <Sparkles className="w-3.5 h-3.5" /> Live: airlines, airports, rail, bus terminals, ferries & seaports — with AI-authored guides.
        </div>
      </section>

      {/* One-click browse chips (types + country pairs + quick jumps) */}
      <div className="mb-8">
        <TravelChipBar />
      </div>

      {/* Categories */}
      <section className="mb-8">
        <h2 className="font-display text-2xl mb-3">Browse travel</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map(({ icon: Icon, label, to, body, stub }) => (
            <Link key={label} to={to} className="wofy-card !p-4 hover:shadow-md transition-shadow" data-testid={`travel-cat-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg grid place-items-center text-white" style={{ background: "var(--wofy-primary)" }}><Icon className="w-5 h-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg leading-tight flex items-center gap-1.5">
                    {label}
                    {stub && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Coming soon</span>}
                  </div>
                  <p className="text-xs opacity-80 mt-1">{body}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section className="wofy-card !p-6 mb-8">
        <h2 className="font-display text-2xl mb-3">What's coming</h2>
        <ul className="text-sm space-y-2 opacity-90 grid sm:grid-cols-2 gap-x-6">
          <li>✈️ Detailed profiles for every <b>airline, airport, rail station, bus terminal, port</b> — with AI-authored info pages just like our location encyclopedia.</li>
          <li>🏨 Verified hotels, resorts, homestays, vacation rentals with rooms, amenities, availability + booking inquiry.</li>
          <li>🧳 Tour packages, itineraries, family/pilgrimage/adventure/luxury tours with reviews.</li>
          <li>🗺️ AI Trip Planner — generate 3-day itineraries, honeymoons, budget trips.</li>
          <li>🚕 Local experiences — cooking classes, village tours, safaris, heritage walks.</li>
          <li>💰 Travel deals — early-bird, last-minute, family, senior, student discounts.</li>
        </ul>
      </section>

      {/* Register CTA for travel businesses */}
      <section
        className="rounded-xl p-6 md:p-8 flex flex-wrap items-center justify-between gap-4"
        style={{ background: "linear-gradient(135deg, rgba(178,93,56,0.08), rgba(178,93,56,0.02))" }}
      >
        <div className="min-w-0">
          <div className="overline mb-1">TRAVEL BUSINESSES</div>
          <h3 className="font-display text-2xl leading-tight">Grow your travel business on Social Pulse</h3>
          <p className="text-sm opacity-80 mt-1 max-w-lg">
            Hotels, resorts, tour operators, guides, transport providers — get a verified page,
            unlimited listings, photo galleries, customer reviews, and direct inquiries. Free tier
            available.
          </p>
        </div>
        <Link to="/businesses/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="travel-register-cta">
          Register your travel business <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
